import { IPosition } from "../shared/types";

export interface VolumeDataPoint {
  /** Bucket key in UTC: "YYYY-MM-DD" for a day or a week (its Sunday), "YYYY-MM" for a month. */
  date: string;
  volume: number;
}

export type VolumeTimeframe = "daily" | "weekly" | "monthly";

const DAY_MS = 86_400_000;

/**
 * Pool fee tier as a fraction. Rows store it either as a fraction (0.003) or in millionths like
 * Uniswap tiers (500 / 3000 / 10000 = 0.05% / 0.3% / 1%); missing → 0.3%. Same rule as the
 * backend's ReportService.
 */
export const poolFeeFraction = (poolFee?: number): number =>
  !poolFee ? 0.003 : poolFee > 1 ? poolFee / 1e6 : poolFee;

/** Fees earned by a position in USD, at its closing prices (opening ones as a fallback). */
const feesUSD = (p: IPosition): number => {
  const prices = p.balanceSnapshotEnd || p.balanceSnapshotStart;
  if (!prices) return 0;
  return (
    (Number(p.feesObtainedToken0) || 0) * (prices.token0Price || 0) +
    (Number(p.feesObtainedToken1) || 0) * (prices.token1Price || 0)
  );
};

/** Volume a position provided, implied by its fees: fees / fee tier. */
export const positionVolumeUSD = (p: IPosition): number => {
  const fee = poolFeeFraction(p.poolFee);
  const volume = fee > 0 ? feesUSD(p) / fee : 0;
  return Number.isFinite(volume) ? volume : 0;
};

/** Rows closed with "SKIPPED_ALREADY_CLOSED" were never closed on-chain: still open (backend rule). */
const isEffectivelyOpen = (p: IPosition): boolean =>
  p.isOpen || p.closeTxHash === "SKIPPED_ALREADY_CLOSED";

/**
 * Volume per UTC day (keyed by its midnight, ms). Each position's volume is split across the days
 * it was open in proportion to the time it spent in each, so it adds up to exactly its volume
 * whatever its length; open positions run until `now`.
 */
const volumeByUTCDay = (positions: IPosition[], now: number): Map<number, number> => {
  const daily = new Map<number, number>();
  const add = (day: number, volume: number) => daily.set(day, (daily.get(day) || 0) + volume);

  positions.forEach((p) => {
    const volume = positionVolumeUSD(p);
    const start = new Date(p.date).getTime();
    if (!volume || !Number.isFinite(start)) return;
    const closedAt = new Date(p.endDate).getTime();
    // A closed row without a valid end date counts on its start (as the backend does).
    const end = Math.max(start, isEffectivelyOpen(p) ? now : closedAt > 0 ? closedAt : start);
    const firstDay = Math.floor(start / DAY_MS) * DAY_MS;

    if (end === start) {
      add(firstDay, volume);
      return;
    }
    for (let day = firstDay; day < end; day += DAY_MS) {
      const overlap = Math.min(end, day + DAY_MS) - Math.max(start, day);
      add(day, (volume * overlap) / (end - start));
    }
  });

  return daily;
};

/** UTC midnight starting the bucket that holds `day`: the day itself, its week (Sunday) or its month. */
const bucketStart = (day: number, timeframe: VolumeTimeframe): number => {
  if (timeframe === "daily") return day;
  const d = new Date(day);
  return timeframe === "weekly"
    ? day - d.getUTCDay() * DAY_MS
    : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

const nextBucketStart = (start: number, timeframe: VolumeTimeframe): number => {
  if (timeframe === "daily") return start + DAY_MS;
  if (timeframe === "weekly") return start + 7 * DAY_MS;
  const d = new Date(start);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
};

const bucketKey = (start: number, timeframe: VolumeTimeframe): string => {
  const isoDay = new Date(start).toISOString().slice(0, 10);
  return timeframe === "monthly" ? isoDay.slice(0, 7) : isoDay;
};

/**
 * Volume provided per UTC day, week (from Sunday) or month, from the first bucket with volume to
 * the last one (today's while a position is open), gaps filled with zero. The buckets add up to
 * calculateTotalVolume. Everything is in UTC, so the figures do not depend on the viewer's timezone.
 */
export const processVolumeData = (
  positions: IPosition[],
  timeframe: VolumeTimeframe
): VolumeDataPoint[] => {
  if (!positions || positions.length === 0) return [];

  const buckets = new Map<number, number>();
  volumeByUTCDay(positions, Date.now()).forEach((volume, day) => {
    const start = bucketStart(day, timeframe);
    buckets.set(start, (buckets.get(start) || 0) + volume);
  });
  if (buckets.size === 0) return [];

  const starts = Array.from(buckets.keys());
  const last = Math.max(...starts);
  const series: VolumeDataPoint[] = [];
  for (let start = Math.min(...starts); start <= last; start = nextBucketStart(start, timeframe)) {
    series.push({ date: bucketKey(start, timeframe), volume: buckets.get(start) || 0 });
  }
  return series;
};
